import React from "react";
import { Check, Zap, Users, Shield, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const models = [
  {
    id: "sprint",
    badge: "FASTEST TIMELINE",
    badgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    name: "Fixed-Scope MVP Sprint",
    price: "$3,500",
    period: "fixed price / project",
    timeline: "2 to 4 Weeks Delivery",
    desc: "For startups & founders who need a production-grade web/mobile MVP or AI Voice Agent launched rapidly with zero scope creep.",
    features: [
      "Fixed deliverables & milestone roadmap",
      "Full frontend, backend & database architecture",
      "AI Voice SDR or LLM workflow integration",
      "Cloud deployment (AWS / Oracle / Cloudflare)",
      "30 days post-launch warranty & bug fixes",
    ],
    popular: false,
    cta: "Start MVP Sprint",
  },
  {
    id: "pod",
    badge: "MOST POPULAR",
    badgeColor: "bg-purple-500/20 text-purple-300 border-purple-500/40",
    name: "Dedicated Engineering Pod",
    price: "$4,500",
    period: "per month / dedicated squad",
    timeline: "Ongoing Monthly Retainer",
    desc: "For scaling companies needing a dedicated team of senior full-stack developers and AI architects integrated into your Slack.",
    features: [
      "1 Lead AI Architect + 2 Senior Full-Stack Engineers",
      "Direct integration into your Slack, Jira & GitHub",
      "Daily asynchronous standups & weekly sprint demos",
      "Continuous CI/CD feature shipping",
      "Flexible scaling — scale up or pause anytime",
    ],
    popular: true,
    cta: "Hire Dedicated Pod",
  },
  {
    id: "ops",
    badge: "ENTERPRISE SLA",
    badgeColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    name: "Managed Sovereign Operations",
    price: "$1,500",
    period: "per month / 24/7 SLA",
    timeline: "Annual / Quarterly Retainer",
    desc: "For businesses with live AI voice agents and mission-critical cloud infrastructure requiring 24/7 monitoring and model calibration.",
    features: [
      "24/7/365 uptime & latency monitoring",
      "Continuous LLM prompt tuning & objection logic updates",
      "Database scaling, indexing & automated backups",
      "Sub-1-hour emergency response SLA",
      "Monthly performance & cost-optimization audits",
    ],
    popular: false,
    cta: "Deploy Managed Ops",
  },
];

const EngagementModels = () => {
  return (
    <section className="py-24 bg-slate-50 dark:bg-[#07090e] border-y border-slate-200/80 dark:border-white/5 relative overflow-hidden">
      {/* Background ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="wrapper relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
            <Sparkles size={14} /> Transparent Engagement Models
          </div>
          <h2 data-aos="fade-up" className="section-heading dark:text-white">
            Flexible Partnerships Built for Speed & Scale
          </h2>
          <p data-aos="fade-up" className="text-slate-600 dark:text-slate-400 text-base sm:text-lg">
            Choose the engagement model that matches your growth stage. No hidden fees, no opaque hourly billing, and 100% intellectual property ownership.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {models.map((model, idx) => (
            <div
              key={model.id}
              data-aos="fade-up"
              data-aos-delay={idx * 100}
              className={`relative rounded-3xl p-8 flex flex-col justify-between transition-all duration-300 ${
                model.popular
                  ? "bg-white dark:bg-[#0c0f17] border-2 border-primary shadow-2xl shadow-primary/10 -translate-y-2"
                  : "bg-white dark:bg-[#0c0f17] border border-slate-200 dark:border-white/10 shadow-lg hover:border-slate-300 dark:hover:border-white/20"
              }`}
            >
              {model.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-primary to-purple-600 text-white shadow-md uppercase tracking-wider">
                  Recommended For Scaling
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${model.badgeColor}`}>
                    {model.badge}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-3">{model.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-medium">{model.timeline}</p>
                </div>

                <div className="pt-2 pb-4 border-y border-slate-100 dark:border-white/5">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white font-mono">{model.price}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">/ {model.period}</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
                    {model.desc}
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider">
                    What's Included:
                  </p>
                  <ul className="space-y-2.5">
                    {model.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                        <Check size={16} className="text-primary flex-shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-4">
                <Link
                  to="/contact"
                  className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                    model.popular
                      ? "primary-btn text-white shadow-lg shadow-primary/20"
                      : "bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10"
                  }`}
                >
                  <span>{model.cta}</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EngagementModels;
